import React from "react";

import { useGetFormattedEmployeeDataQuery } from "EmployerApplication/api";
import { dollarDisplay } from "EmployerApplication/CostEstimationUtils";
import { useSelector } from "react-redux";

import { Body1, Body2, SubBody } from "Cobalt/BodyText";
import { Table, TH, TD, TR, TDFoot, ExpandingRow } from "Cobalt/Table";
import { formatEmployeeProfileAsInitialValues } from "Common/EnterAges/Components/Helpers/FormHelpers";
import { Row, Col } from "Common/Grid";
import { Div } from "Common/HTML";
import { SizeSwitch } from "Common/Responsive";

import makeBem from "Utils/makeBem";

const bem = makeBem("LifeCostBreakdownBody");

const MobileTable = ({ employees }) => (
  <Table>
    <thead>
      <TR>
        <TH>Employee</TH>
        <TH>Age</TH>
        <TH />
      </TR>
    </thead>
    <tbody className={bem()}>
      {employees.map((employee, index) => (
        <ExpandingRow
          key={index}
          mainContent={
            <>
              <TD>{employee.name}</TD>
              <TD>{employee.age}</TD>
            </>
          }
          expandingContent={
            <>
              <TR>
                <TD>Coverage Amount</TD>
                <TD className="text-right">{dollarDisplay(employee.coverageAmount || null)}</TD>
                <TD />
              </TR>
              <TR>
                <TD>Total Cost</TD>
                <TD className="text-right">{dollarDisplay(employee.employeeCost || null)}</TD>
                <TD />
              </TR>
            </>
          }
        />
      ))}
    </tbody>
  </Table>
);

const DesktopTable = ({ employees, totalCost }) => (
  <Table>
    <thead>
      <TR>
        <TH>Employee</TH>
        <TH>Age</TH>
        <TH className="text-right">Coverage Amount</TH>
        <TH className="text-right">Total Cost</TH>
        <TH />
      </TR>
    </thead>
    <tbody className={bem()}>
      {employees.map((employee, index) => (
        <TR key={index}>
          <TD>{employee.name}</TD>
          <TD>{employee.age}</TD>
          <TD className="text-right">{dollarDisplay(employee.coverageAmount || null)}</TD>
          <TD className="text-right">{dollarDisplay(employee.employeeCost || null)}</TD>
          <TD />
        </TR>
      ))}
    </tbody>
    <tfoot>
      <TR>
        <TDFoot colSpan={3} />
        <TDFoot className="text-right">{dollarDisplay(totalCost)}</TDFoot>
      </TR>
    </tfoot>
  </Table>
);

const applyCostToEmployees = (employees, coverageAmount, employeeCost) =>
  employees.map((employee) => {
    const updatedEmployee = { ...employee }; // because the employee object is non-extensible

    if (employee.age >= 70) {
      updatedEmployee.coverageAmount = coverageAmount * 0.5;
    } else if (employee.age >= 65) {
      updatedEmployee.coverageAmount = coverageAmount * 0.65;
    } else {
      updatedEmployee.coverageAmount = coverageAmount;
    }

    updatedEmployee.employeeCost = employeeCost;
    return updatedEmployee;
  });

export default function LifeCostBreakdownBody({ plan }) {
  const employeeProfile = useSelector((state) => state.employeeProfile);
  const { data } = useGetFormattedEmployeeDataQuery();
  const formattedEmployeeData = data?.formatted_employee_data || [];
  const employeeCost = plan.average_monthly_premium;
  const coverageAmount = plan.coverage_amount;
  const numberOfEmployees = formattedEmployeeData.length > 0 ? formattedEmployeeData.length : employeeProfile.length;
  const totalCost = employeeCost * numberOfEmployees;
  const employees = applyCostToEmployees(
    formattedEmployeeData.length > 0 ? formattedEmployeeData : formatEmployeeProfileAsInitialValues(employeeProfile),
    coverageAmount,
    employeeCost,
  );
  return (
    <>
      <Row pb={4}>
        <SizeSwitch
          xs={
            <>
              <Col sm={8}>
                <Body2>You Pay {plan?.carrier}</Body2>
              </Col>
              <Col xs={12}>
                <Body2 pt={1}>
                  {dollarDisplay(totalCost)}
                  <Div pl={2} className={bem("perMonth")}>
                    Per Month
                  </Div>
                </Body2>
              </Col>
            </>
          }
          sm={
            <>
              <Col sm={8}>
                <Body1>You Pay {plan?.carrier}</Body1>
              </Col>
              <Col sm={4}>
                <Body2 className="text-right">
                  {dollarDisplay(totalCost)}
                  <Div pl={2} className={bem("perMonth")}>
                    Per Month
                  </Div>
                </Body2>
              </Col>
            </>
          }
        />
      </Row>
      <Row pb={2}>
        <Col xs={12} sm={8}>
          <SubBody pb={2}>All Life Insurance plans require that the monthly premium is 100% covered by the employer.</SubBody>
          <SubBody pb={4}>Coverage amounts are reduced for employees ages 65 and older.</SubBody>
        </Col>
      </Row>
      <SizeSwitch xs={<MobileTable employees={employees} />} sm={<DesktopTable employees={employees} totalCost={totalCost} />} />
    </>
  );
}
